/**
 * ringtone — the incoming-call ring, synthesised.
 *
 * ── Why synthesised, not an asset ────────────────────────────────────────────
 * There is no audio file to ship, nothing to fetch over a slow link, and the
 * cadence is deterministic. It also matches the convention this codebase already
 * uses for its notification chime (`notificationSound.ts`), including the rule
 * that matters most here: **a browser that refuses to play audio is reported as
 * ok-or-not, never as "ringing"**.
 *
 * ── Contract ─────────────────────────────────────────────────────────────────
 *  - `startRingtone()` is IDEMPOTENT: calling it while already ringing returns
 *    true and does NOT create a second loop, so a duplicate socket event cannot
 *    produce two overlapping rings;
 *  - it returns FALSE when audio was not actually started (no AudioContext, or
 *    the context is still suspended by autoplay policy) — the caller must treat
 *    that as "not ringing" and must not retry in a loop;
 *  - `stopRingtone()` is idempotent and always safe to call, including when
 *    nothing is ringing;
 *  - `isRingtonePlaying()` reports whether the loop is actually running, which
 *    is what the UI keys its honest state off.
 *
 * Ringing a phone is a user-visible intrusion, so it is switchable and the
 * preference is stored; it is deliberately independent of the notification
 * chime so silencing one does not silence the other.
 */

/** Time between ring bursts. */
export const RING_CADENCE_MS = 2000;
/** Duration of each two-tone burst. */
export const RING_BURST_MS = 1200;
/** How long an unanswered ring may run before it is treated as missed. */
export const RING_TIMEOUT_MS = 45_000;
/** User preference key (on by default). */
export const RINGTONE_STORAGE_KEY = 'bhealth_ringtone';

let ctx: AudioContext | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let activeOscillators: OscillatorNode[] = [];
let gestureBound = false;

/** User preference — the ring is on unless explicitly switched off. */
export function isRingtoneEnabled(): boolean {
  try {
    const raw = localStorage.getItem(RINGTONE_STORAGE_KEY);
    return raw === null ? true : raw === 'true';
  } catch {
    return true;
  }
}

export function setRingtoneEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(RINGTONE_STORAGE_KEY, enabled ? 'true' : 'false');
  } catch {
    /* preference is best-effort */
  }
}

function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) {
    try {
      ctx = new Ctor();
    } catch {
      ctx = null;
    }
  }
  return ctx;
}

/**
 * Try to make audio available. Only a user gesture can lift a browser's autoplay
 * block, so this is called from the accept button and from the first gesture.
 * Returns whether the context is actually running afterwards.
 */
export function unlockRingtone(): boolean {
  const c = getContext();
  if (!c) return false;
  if (c.state === 'suspended') {
    c.resume().catch(() => { /* still blocked — the caller reports honestly */ });
  }
  return c.state === 'running';
}

/** Attach one-shot gesture listeners so the ring can become audible at all. */
export function primeRingtoneOnFirstGesture(): () => void {
  if (typeof window === 'undefined' || gestureBound) return () => {};
  gestureBound = true;
  const handler = () => {
    unlockRingtone();
    window.removeEventListener('pointerdown', handler);
    window.removeEventListener('keydown', handler);
  };
  window.addEventListener('pointerdown', handler, { once: true });
  window.addEventListener('keydown', handler, { once: true });
  return () => {
    gestureBound = false;
    window.removeEventListener('pointerdown', handler);
    window.removeEventListener('keydown', handler);
  };
}

/** One two-tone burst. Returns the oscillators so they can be stopped. */
function ringBurst(c: AudioContext): OscillatorNode[] {
  const now = c.currentTime;
  const seconds = RING_BURST_MS / 1000;

  const gain = c.createGain();
  gain.connect(c.destination);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.14, now + 0.04);
  gain.gain.setValueAtTime(0.14, now + Math.max(0.1, seconds - 0.12));
  gain.gain.exponentialRampToValueAtTime(0.0001, now + seconds);

  // 440 + 480 Hz together read as a plain telephone ring rather than a melody.
  return [440, 480].map((freq) => {
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now);
    osc.connect(gain);
    osc.start(now);
    osc.stop(now + seconds + 0.02);
    return osc;
  });
}

/**
 * Start ringing. Returns true only when a ring actually began.
 * Idempotent: an already-ringing caller gets `true` and no second loop.
 */
export function startRingtone(): boolean {
  if (!isRingtoneEnabled()) return false;
  if (timer !== null) return true; // already ringing — never stack a second loop

  const c = getContext();
  // Autoplay policy (or a browser with no Web Audio at all): report failure
  // rather than pretending the phone is ringing.
  if (!c || c.state !== 'running') return false;

  const fire = () => {
    try {
      activeOscillators = ringBurst(c);
    } catch {
      // A burst that cannot be scheduled must not break the caller.
    }
  };
  fire();
  timer = setInterval(fire, RING_CADENCE_MS);
  return true;
}

/** Stop ringing. Idempotent and safe to call from any lifecycle path. */
export function stopRingtone(): void {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
  for (const osc of activeOscillators) {
    try {
      osc.stop();
    } catch {
      /* already stopped */
    }
    try {
      osc.disconnect();
    } catch {
      /* already disconnected */
    }
  }
  activeOscillators = [];
}

/** Is the ring loop actually running right now? */
export function isRingtonePlaying(): boolean {
  return timer !== null;
}
